import { Injectable } from "@angular/core";
import { ActivatedRoute, NavigationEnd, Route, Router } from "@angular/router";
import { BehaviorSubject, filter, map, merge } from "rxjs";
import { RequestApprovalTypeDetailsStore } from "../store/approval-type-details-store";
import { ApprovalTypesService } from "../approval-type/approval-type";
import { TABS } from "../../enum/request/TABS";
import { MenuItem } from "primeng/api";
import { TranslateService } from "@ngx-translate/core";


@Injectable({
    providedIn: 'root'
})
export class ProgressService {

    tabsText: any;

    private activeTabsSubject = new BehaviorSubject<MenuItem[]>([]);
    activeTabs$ = this.activeTabsSubject.asObservable();

    private currentStepSubject = new BehaviorSubject<number>(1);
    currentStep$ = this.currentStepSubject.asObservable();

    constructor(private router: Router
        , private activatedRoute: ActivatedRoute
        , private requestApprovalTypeDetailsStore: RequestApprovalTypeDetailsStore
        , private approvalTypesService: ApprovalTypesService
        , private translateService: TranslateService) {


        this.initText();
    }


    initText() {
        this.translateService.stream('tabs').subscribe(translation => {
            this.tabsText = translation;
            this.subscribeToProgressUpdates();
        });
    }
    private subscribeToProgressUpdates(): void {

        merge(
            this.router.events.pipe(
                filter(event => event instanceof NavigationEnd),
                map(event => ({ value: event, source: 'router' }))
            ),
            this.requestApprovalTypeDetailsStore.getApprovalTabs().pipe(
                map(value => ({ value, source: 'approvalTabs' })))
        )
            .subscribe((data) => {
                //data = event:NavigationEnd | approvalTabs,approvalTabs: TABS[] | number[]
                this.updateActiveTabs(data);
                this.updateCurrentStep();
            });

    }

    private updateActiveTabs(data: any) {
        const isInProductTab = data.source === 'router' && this.router.url.includes('/product/');
        const isTabListUpdated = data.source === 'approvalTabs' && data.value.length;

        if (isInProductTab) {
            this.updateActiveTabsByProduct()
        }
        else if (isTabListUpdated) {
            this.updateActiveTabsByRoute();
        }

    }

    private updateActiveTabsByRoute(): void {
        setTimeout(() => {
            const currentTabs = this.activeTabsSubject.getValue();
            const routConfig = this.router.config;
            const isNeedTabToProgress = (route: Route) => {
                return route.data &&
                    route.data['stepCondition'] &&
                    (route.data['stepCondition'] === true ||
                        (route.data['stepCondition'] === 'isExistTab' &&
                            this.approvalTypesService.isExistTabToApproval(route.data['step'])));
            };

            const routesToProgress = routConfig.filter(route => isNeedTabToProgress(route));

            let menuTabToProgress: MenuItem[] = routesToProgress.map(route => ({
                step: route.data!['step'],
                path: route['path']!,
                label: this.tabsText[route.data!['title']?.toString()],
                styleClass: ''
            }));

            const existingDynamicProducts = currentTabs.filter(tab => tab['step'] === TABS.PRODUCTS && !!tab['guid']);

            if (existingDynamicProducts.length > 0) {
                menuTabToProgress = menuTabToProgress.filter(tab => tab['step'] !== TABS.PRODUCTS);
                menuTabToProgress.push(...existingDynamicProducts);
            }

            menuTabToProgress.sort((a, b) => (a['step'] || 0) - (b['step'] || 0));

            if (menuTabToProgress.length > 0) {
                menuTabToProgress.forEach(t => t.styleClass = '');
                menuTabToProgress[menuTabToProgress.length - 1].styleClass = 'mi-last-item';
            }

            this.activeTabsSubject.next(menuTabToProgress);
        });
    }

    private updateActiveTabsByProduct(): void {
        const tabs = this.activeTabsSubject.getValue();

        const activeGuid = this.getDeepestRouteParam('guid') as string;

        const activeTab = tabs.find(tab => tab['guid'] === activeGuid);
       
        const productsTab = tabs.filter(tab => tab['step'] === TABS.PRODUCTS && !!tab['guid']);
        if (!activeTab) {
            tabs.push({
                step: TABS.PRODUCTS,
                path: '/product',
                guid: activeGuid,
                label: this.tabsText['products'] + ' ' + (productsTab.length + 1)
            })
        }
        const updatedTabs = tabs.filter(tab => tab['step'] !== TABS.PRODUCTS || !!tab['guid']);
        updatedTabs.sort((a, b) => a['step'] - b['step'])

        this.activeTabsSubject.next(updatedTabs);

    }

    private getDeepestRouteParam(paramName: string): string | null {
        let currentRoute = this.activatedRoute.root;
        while (currentRoute.firstChild) {
            currentRoute = currentRoute.firstChild;
        }
        return currentRoute.snapshot.paramMap.get(paramName);
    }

    private updateCurrentStep(): void {
        const currentUrl = this.router.url;
        const tabs = this.activeTabsSubject.getValue();

        const isCurrentTab = (tab: MenuItem) => {
            if (tab['guid']) {
                return currentUrl.includes(tab['path']) && currentUrl.includes(tab['guid'])
            }
            else {
                return currentUrl.includes(tab['path'])
            }
        }

        const currentTab = tabs.find(tab => isCurrentTab(tab));

        if (currentTab) {
            const index = tabs.indexOf(currentTab) + 1;
            this.currentStepSubject.next(index);
        }
    }

    removeProductTab(guid: string) {
        const tabs = this.activeTabsSubject.getValue();
        const updatedTabs = tabs.filter(tab => tab['guid'] !== guid);

        const productTabs = updatedTabs.filter(tab => tab['step'] === TABS.PRODUCTS && !!tab['guid']);
        productTabs.forEach((tab, index) => {
            tab.label = this.tabsText['products'] + ' ' + (index + 1);
        });

        this.activeTabsSubject.next(updatedTabs);
    }

    resetTabs() {        
        this.activeTabsSubject.next([]);        
        this.currentStepSubject.next(1);    
    }
}
